import React from "react";
import {
    AspectRatio,
    Badge,
    Box,
    Center,
    Icon,
    Image,
    Skeleton,
    Text,
    Flex,
} from "@chakra-ui/react";
import {FiMapPin} from "react-icons/fi";
import {useTranslation} from "react-i18next";

interface SiteCardProps {
    id: string;
    name: string;
    siteImage?: string | null;
    freeCount?: number;
    isSelected: boolean;
    isCountLoading: boolean;
    isClosed?: boolean;
    isDisabled?: boolean;
    onClick: () => void;
}

const SiteCard: React.FC<SiteCardProps> = ({
    id,
    name,
    siteImage,
    freeCount,
    isSelected,
    isCountLoading,
    isClosed = false,
    isDisabled = false,
    onClick,
}) => {
    const {t} = useTranslation();

    return (
        <Box
            borderWidth={isSelected ? 2 : 1}
            borderColor={isSelected ? "custom.brand_primary" : "custom.gray_300"}
            borderRadius={"md"}
            overflow={"hidden"}
            cursor={isDisabled ? "not-allowed" : "pointer"}
            bg={isSelected ? "custom.gray_100" : "white"}
            opacity={isDisabled ? 0.5 : 1}
            pointerEvents={isDisabled ? "none" : "auto"}
            onClick={isDisabled ? undefined : onClick}
            transition={"all 0.2s"}
            _hover={isDisabled ? {} : {borderColor: "custom.brand_primary", shadow: "sm"}}
        >
            <AspectRatio ratio={16 / 9}>
                {siteImage ? (
                    <Image
                        src={`data:image/png;base64,${siteImage}`}
                        objectFit={"cover"}
                        alt={name}
                    />
                ) : (
                    <Center bg={"custom.gray_100"}>
                        <Icon as={FiMapPin} boxSize={8} color={"custom.gray_400"}/>
                    </Center>
                )}
            </AspectRatio>
            <Box p={3}>
                <Text fontWeight={"semibold"} fontSize={"sm"} mb={2} noOfLines={1}>
                    {name}
                </Text>
                {isClosed ? (
                    <Badge colorScheme={"gray"} fontSize={"xs"}>
                        {t("newReservation.closed")}
                    </Badge>
                ) : isDisabled ? (
                    isCountLoading ? (
                        <Skeleton height={"16px"} width={"80px"}/>
                    ) : (
                        <Flex align={"center"} gap={1}>
                            <Box w={"8px"} h={"8px"} borderRadius={"full"} bg={"red.500"} flexShrink={0}/>
                            <Text fontSize={"xs"} color={"red.500"} fontWeight={"medium"}>
                                {t("newReservation.noFreeSpots")}
                            </Text>
                        </Flex>
                    )
                ) : freeCount !== undefined && (
                    isCountLoading ? (
                        <Skeleton height={"16px"} width={"80px"}/>
                    ) : (
                        <Badge colorScheme={"green"} fontSize={"xs"}>
                            {freeCount === 1
                                ? t("newReservation.freePlace", {amount: freeCount})
                                : t("newReservation.freePlaces", {amount: freeCount})}
                        </Badge>
                    )
                )}
            </Box>
        </Box>
    );
};

export default SiteCard;
